import React from 'react';
import Text from './components/Text';
import Index from './components/Index';
import List from './components/List';
import styled from 'styled-components';
import Connection from './components/Connection';
import Composite from './components/Composite';
import Trajectory from './components/Trajectory';
import Polygon from './components/Polygon';
import DateCtl from './components/Date';
import DateTimeCtl from './components/DateTime';
import Number from './components/Number';
import Boolean from './components/Boolean';
import TimeMinutes from './components/TimeMinutes';
import ConnectionList from './components/ConnectionList';
import ColorCtrl from './components/Color';
import { IDataAttributes } from 'types/dataAttributes';
import CameraSelection from './components/CameraSelection';
import BooleanText from "../../../../../../pages/Handbooks/SpreadSheetHandbook/components/BooleanText/BooleanText";
import TemperatureSensorSelection from './components/TemperatureSensorSelection';
import ConnectionComboBox from './components/ComboBox';

interface IProps {
  field: any;
  value: any;
  disabled: boolean;
  row: any;
  config: any;
  errorMsg?: string;
  connection?: {
    config: any;
    list: any[];
  };
  onValueChange?: (field: any, value: any) => void;
  onIndexChange?: (field: any, value: any) => void;
}

const ErrorMsg = styled.div`
  position: absolute;
  color: #c00;
  left: 0;
  bottom: 0;
  font-size: 10px;
  width: 100%;
`;

class TCell extends React.Component<IProps> {
  onFieldChange = (value: any): void => {
    this.props.onValueChange &&
      this.props.onValueChange(
        this.props.field,
        value ?? null
      );
  };

  onIndexChange = (index_value: any): void => {
    this.props.onIndexChange &&
      this.props.onIndexChange(this.props.field, index_value);
  };

  getDynamicDataAttr = (field: any, row: any):IDataAttributes => {
    let props = {} as IDataAttributes;
    const key = `data-${(field.name).toLowerCase()}-id`;
    const dataAttrKey = key as keyof IDataAttributes;
    props[dataAttrKey] = row.id;
    props['readOnly'] = field.disabled;

    return props
  };

  getField = (): React.ReactNode => {
    const { field, value, disabled, row } = this.props;
    switch (true) {
      case field.index:
        return (
          <Index
            value={value}
            index_value={row['__index__' + field.name]}
            edited={field.edited}
            onChange={this.onIndexChange}
            readonly={disabled}
          />
        );
      case field.type === 'cameraModal':
        return <CameraSelection dvr_id={row.id} vehicle_id={row.vehicle_id} config={this.props.config} cameraTypes={this.props.connection?.list ?? []} />;
      case field.type === 'temperatureSensorModal':
        return <TemperatureSensorSelection vehicle_id={row.id} parentConfig={this.props.config} temperatureSensorTypes={this.props.connection?.list ?? []} />;
      case field.type === 'connetioncombobox':
        return (
          <ConnectionComboBox
            value={value}
            field={field}
            onChange={this.onFieldChange}
            connection={this.props.connection}
            readonly={disabled}
            row={row}
            dataAttrQA={this.getDynamicDataAttr(field, row)}
          />
        );
      case !!this.props.connection && !field.relatedField:
        const dataAttrProps = this.getDynamicDataAttr(field, row);
        return (
          <Connection
            value={value}
            onChange={this.onFieldChange}
            connection={this.props.connection}
            readonly={disabled}
            dataAttrQA={dataAttrProps}
          />
        );
      case field.type === 'list':
        return (
          <List
            value={value}
            onChange={this.onFieldChange}
            list={field.values}
            readonly={disabled}
          />
        );
      case field.type === 'composite':
        return (
          <Composite
            row={row}
            field={field}
            onChange={this.onFieldChange}
            value={value}
            config={this.props.config}
            onValueChange={this.props.onValueChange}
            readonly={disabled}
          />
        );
      case field.type === 'connectionlist':
        const attrProps = this.getDynamicDataAttr(field, row);
        return (
          <ConnectionList
            value={value}
            field={field}
            onChange={this.onFieldChange}
            connection={this.props.connection}
            readonly={disabled}
            row={row}
            dataAttrQA={attrProps}
          />
        );
      case field.type === 'trajectory':
        return <Trajectory value={value} onChange={this.onFieldChange} readonly={disabled} />;
      case field.type === 'polygon':
        return <Polygon value={value} onChange={this.onFieldChange} isPolygon={field.isPolygon} />;
      case field.type === 'date':
        const dataQAProps = this.getDynamicDataAttr(field, row);
        return (
          <DateCtl
            value={value}
            onChange={this.onFieldChange}
            readonly={disabled}
            dataAttrQA={dataQAProps}
          />
        );
      case field.type === 'datetime':
        return (
          <DateTimeCtl
            value={value}
            onChange={this.onFieldChange}
            readonly={disabled}
          />
        );
      case field.type === 'number':
      case field.type === 'bignumber':
        const dataProps = this.getDynamicDataAttr(field, row);
        return <Number value={value} onChange={this.onFieldChange} field={field} dataAttr={dataProps} />;
      case field.type === 'boolean':
        return (
          <Boolean
            value={value}
            onChange={this.onFieldChange}
            disabled={disabled}
            readonly={disabled}
          />
        );
      case field.type === 'timeminutes':
        return <TimeMinutes value={value} onChange={this.onFieldChange} readonly={disabled} />;
      case field.type === 'color':
        return <ColorCtrl value={value} onChange={this.onFieldChange} readonly={disabled} labelAsText={field.labelAsText}/>;
      case field.type === 'booleantext':
        return <BooleanText value={value} fieldTitle={field.title} />
    }
    
    return (
      <Text
        value={field.relatedField ? this.props.connection?.list.find(item => item.id === row?.[field.relatedField])?.[field.name] : value}
        onChange={this.onFieldChange}
        field={field}
        readOnly={disabled}
        connection={this.props.connection ?? undefined} />
    );
  };

  render() {
    return (
      <td>
        {this.getField()}
        {this.props.errorMsg && (
          <ErrorMsg onClick={() => this.onFieldChange(this.props.value)}>
            {this.props.errorMsg}
          </ErrorMsg>
        )}
      </td>
    );
  }
}

export default TCell;
